// framework/client/helpers/MessagingClient/avatarHelper.js

if (typeof document !== 'undefined') {
    const styleId = 'app-avatar-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
            .app-avatar {
                display: inline-flex;
                /* Use inline-flex to flow well with text and other elements */
                align-items: center;
                justify-content: center;
                vertical-align: middle;
                /* Helps align with text */
                border-radius: 50%;
                /* Makes the container circular */
                font-weight: 600;
                /* Bolder text for initials */
                color: #ffffff;
                /* Default text color is white, assuming darker backgrounds */
                overflow: hidden;
                /* Crucial for ensuring the child <img> respects the border-radius */
                flex-shrink: 0;
                /* Prevents the avatar from shrinking in a flex container */
                text-transform: uppercase;
                /* Ensures initials are always uppercase */
                background-color: var(--bs-secondary, #6c757d);
            }
            .app-avatar img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                /* Ensures the image covers the circular area without distortion */
            }
            .app-avatar.app-avatar-default {
                width: 40px;
                height: 40px;
                font-size: 1.2rem;
            }
            .app-avatar.app-avatar-small {
                width: 32px;
                height: 32px;
                font-size: 1rem;
            }
            .app-avatar.app-avatar-medium {
                width: 50px;
                height: 50px;
                font-size: 1.5rem;
            }
            .app-avatar.app-avatar-large {
                width: 100px;
                height: 100px;
                font-size: 3rem;
                border: 3px solid var(--bs-border-color);
            }
        `;
        document.head.appendChild(style);
    }
}

/**
 * Generates a deterministic, visually appealing background color from a string.
 */
function generateColorFromName(name) {
    if (!name) return null;
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
        hash = hash & hash;
    }
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 65%, 44%)`; 
}

function getAvatarInitial(displayName, fallback = '?') {
    const text = String(displayName || '').trim();
    return text.match(/[A-Za-z0-9]/)?.[0]?.toUpperCase() || fallback;
}

/**
 * Creates and returns an HTML div element representing an avatar.
 */
export function createAvatarElement({ 
    displayName = '?', 
    avatarUrl = null, 
    sizeClass = 'default',
    size = null,
    fontSize = null,
    title = null,
    additionalClasses = [],
    altText = 'Avatar',
    type = 'user' 
} = {}) {

    const avatarDiv = document.createElement('div');
    avatarDiv.classList.add('app-avatar', `app-avatar-${sizeClass}`, ...additionalClasses);
    // Translation may use the avatar title as a key. Keep the visible initial intact.
    avatarDiv.setAttribute('data-skip-text', 'true');
    if (title || displayName) {
        avatarDiv.title = title || displayName;
    }
    if (displayName) {
        avatarDiv.setAttribute('aria-label', displayName);
    }
    if (size) {
        const cssSize = typeof size === 'number' ? `${size}px` : size;
        avatarDiv.style.width = cssSize;
        avatarDiv.style.height = cssSize;
    }
    if (fontSize) {
        avatarDiv.style.fontSize = typeof fontSize === 'number' ? `${fontSize}px` : fontSize;
    }
    
    let finalAvatarSrc = null;
    if (avatarUrl) {
        if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://') || avatarUrl.startsWith('data:')) {
            finalAvatarSrc = avatarUrl; 
        } else { 
            finalAvatarSrc = `/download/${avatarUrl.replace(/^\//, '')}`;
        }
    }

    if (finalAvatarSrc) {
        const img = document.createElement('img');
        img.src = finalAvatarSrc;
        img.alt = altText || displayName;
        
        img.onerror = () => {
            avatarDiv.innerHTML = ''; 
            const initial = getAvatarInitial(displayName, type === 'group' ? 'G' : '?');
            avatarDiv.textContent = initial;
            avatarDiv.style.backgroundColor = generateColorFromName(displayName); 
        };
        avatarDiv.appendChild(img);
    } else {
        const initial = getAvatarInitial(displayName, type === 'group' ? 'G' : '?');
        avatarDiv.textContent = initial;
        avatarDiv.style.backgroundColor = generateColorFromName(displayName);
    }

    return avatarDiv;
}

// --- FIX: Export an object matching the filename for the bundler ---
export const avatarHelper = {
    createAvatarElement,
    generateColorFromName,
    getAvatarInitial
};
